import { plain } from '../../lib/labels';
import { useState, useEffect } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  type TableColumnsType,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeInvisibleOutlined,
  ReloadOutlined,
  WarningOutlined,
  ApiOutlined,
  PhoneOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUrlState, oneOf } from '../../lib/urlState';
import { QueryError } from '../../lib/QueryState';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';

type Severity = 'info' | 'warning' | 'error' | 'fatal';
type Category = 'crash' | 'network' | 'call' | 'permission' | 'navigation' | 'ui' | 'other';
type Platform = 'ios' | 'android' | 'web' | 'admin';
type Status = 'open' | 'acknowledged' | 'resolved' | 'ignored';
/** Fine-grained cause. `Category` stays the coarse bucket. */
type ErrorType =
  | 'RIVERPOD_LIFECYCLE'
  | 'LISTENERS_API'
  | 'DIO_NETWORK'
  | 'AGORA'
  | 'WEBRTC'
  | 'SOCKET'
  | 'AUTH'
  | 'ADMIN_PANEL'
  | 'BACKEND'
  | 'UNKNOWN';
/** WHICH SYSTEM owns the fault. `agora`/`webrtc` are third party. */
type Source =
  | 'flutter_app'
  | 'backend'
  | 'live_api'
  | 'agora'
  | 'webrtc'
  | 'socket'
  | 'admin_panel'
  | 'unknown';

/** Faults we do not own. Surfaced so they are not triaged as app bugs. */
const THIRD_PARTY: ReadonlySet<Source> = new Set<Source>(['agora', 'webrtc']);

/**
 * A timestamp the API may not have sent. `new Date(null)` is the epoch, so a
 * missing `firstSeenAt` would render a confident "1/1/1970" — stale data shown
 * as real, which an ops console must never do.
 */
function when(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

interface Incident {
  id: string;
  userId: string | null;
  deviceId: string;
  platform: Platform;
  appVersion: string;
  osVersion: string | null;
  category: Category;
  severity: Severity;
  message: string;
  stackTrace: string | null;
  context: Record<string, unknown> | null;
  status: Status;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  createdAt: string;
  ackNote: string | null;
  // Triage fields. Optional throughout: rows written before the
  // incident_triage_fields migration, and clients that have not shipped them
  // yet, simply have none — the table renders "—".
  environment?: string | null;
  buildNumber?: string | null;
  userRole?: string | null;
  feature?: string | null;
  screen?: string | null;
  errorType?: ErrorType | null;
  source?: Source | null;
  fingerprint?: string | null;
  occurrences?: number | null;
  firstSeenAt?: string | null;
  lastSeenAt?: string | null;
}

interface IncidentList {
  items: Incident[];
  nextCursor: string | null;
}

interface Stats {
  open: number;
  fatal24h: number;
  error24h: number;
  warning24h: number;
  byCategory24h: Record<Category, number>;
  last24h: number;
}

const SEVERITY_COLOR: Record<Severity, string> = {
  info: 'default',
  warning: 'warning',
  error: 'error',
  fatal: 'magenta',
};

const CATEGORY_ICON: Record<Category, React.ReactNode> = {
  crash: <WarningOutlined />,
  network: <ApiOutlined />,
  call: <PhoneOutlined />,
  permission: <CloseCircleOutlined />,
  navigation: <AppstoreOutlined />,
  ui: <AppstoreOutlined />,
  other: <AppstoreOutlined />,
};

const STATUS_COLOR: Record<Status, string> = {
  open: 'error',
  acknowledged: 'processing',
  resolved: 'success',
  ignored: 'default',
};

const STATUSES: Status[] = ['open', 'acknowledged', 'resolved', 'ignored'];
const SEVERITIES: Severity[] = ['info', 'warning', 'error', 'fatal'];

export function IncidentsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  // Deep links from the dashboard: ?status=open|…|all, ?severity=, ?window=<minutes>|all,
  // ?incident=<id> (opens that incident). With no params: open incidents, last hour.
  // Filters and the open incident live in the URL (lib/urlState), so a refresh
  // or Back keeps the view and an incident can be linked to.
  const { get, set } = useUrlState();
  const statusParam = get('status');
  const status: Status | undefined =
    statusParam === 'all' ? undefined : STATUSES.includes(statusParam as Status) ? (statusParam as Status) : 'open';
  const setStatus = (v: Status | undefined) => set({ status: v ?? 'all' });
  const severity = oneOf(get('severity'), SEVERITIES);
  const setSeverity = (v: Severity | undefined) => set({ severity: v ?? null });
  const category = (get('category') as Category | null) ?? undefined;
  const setCategory = (v: Category | undefined) => set({ category: v ?? null });
  const platform = (get('platform') as Platform | null) ?? undefined;
  const setPlatform = (v: Platform | undefined) => set({ platform: v ?? null });
  const windowParam = get('window');
  const sinceMinutes: number | undefined =
    windowParam === 'all' ? undefined : windowParam && /^\d+$/.test(windowParam) && Number(windowParam) > 0 ? Number(windowParam) : 60;
  const setSinceMinutes = (v: number | undefined) => set({ window: v ?? 'all' });
  const [selected, setSelected] = useState<Incident | null>(null);
  const linkedId = get('incident');

  // The linked incident may be outside the current filters, so fetch it by id.
  const linked = useQuery({
    queryKey: ['admin', 'incident', linkedId],
    queryFn: async () => (await api.get<Incident>(`/admin/incidents/${linkedId}`)).data,
    enabled: !!linkedId,
  });
  useEffect(() => {
    if (linked.data) setSelected(linked.data);
  }, [linked.data]);
  // Browser Back removed `?incident=`: close the drawer with it.
  useEffect(() => {
    if (!linkedId) setSelected(null);
  }, [linkedId]);

  function openIncident(row: Incident) {
    setSelected(row);
    set({ incident: row.id }, { push: true });
  }
  /** Close the drawer and drop `?incident=` so a refresh does not reopen it. */
  function closeSelected() {
    setSelected(null);
    set({ incident: null });
  }
  const [ackNote, setAckNote] = useState('');

  const stats = useQuery({
    queryKey: ['admin', 'incident-stats'],
    queryFn: async () => (await api.get<Stats>('/admin/incidents/stats')).data,
    refetchInterval: 5_000,
  });

  const list = useQuery({
    queryKey: ['admin', 'incidents', status, severity, category, platform, sinceMinutes],
    queryFn: async () =>
      (
        await api.get<IncidentList>('/admin/incidents', {
          params: { status, severity, category, platform, sinceMinutes, limit: 100 },
        })
      ).data,
    // 5s polling so a fresh fatal incident shows up within the polling
    // window. Triple-paranoid mode would also wire socket.io push for
    // fatal — left as a follow-up; this baseline already meets the user ask.
    refetchInterval: 5_000,
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['admin', 'incidents'] });
    qc.invalidateQueries({ queryKey: ['admin', 'incident-stats'] });
    qc.invalidateQueries({ queryKey: ['admin', 'kpis'] });
  }

  const act = useMutation({
    mutationFn: async (vars: { id: string; verb: 'acknowledge' | 'resolve' | 'ignore'; note?: string }) =>
      (await api.post(`/admin/incidents/${vars.id}/${vars.verb}`, { note: vars.note ?? undefined })).data,
    onSuccess: (_data, vars) => {
      message.success(`Incident ${vars.verb}d`);
      closeSelected();
      setAckNote('');
      invalidate();
    },
    onError: (e: unknown) => {
      message.error(
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ??
          'Could not update incident',
      );
    },
  });

  const columns: TableColumnsType<Incident> = [
    {
      title: 'When',
      dataIndex: 'createdAt',
      render: (s: string) => {
        const d = new Date(s);
        const mins = Math.floor((Date.now() - d.getTime()) / 60_000);
        return mins < 60 ? `${mins}m ago` : d.toLocaleString();
      },
      width: 130,
    },
    {
      title: 'How serious',
      dataIndex: 'severity',
      render: (v: Severity) => <Tag color={SEVERITY_COLOR[v]}>{plain(v)}</Tag>,
      width: 90,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      render: (c: Category) => (
        <Space size={4}>
          {CATEGORY_ICON[c]}
          {c}
        </Space>
      ),
      width: 130,
    },
    {
      title: 'Phone type',
      dataIndex: 'platform',
      render: (p: Platform) => <Tag>{plain(p)}</Tag>,
      width: 90,
    },
    {
      // The triage question, answered in the list rather than three clicks in.
      title: 'Source',
      dataIndex: 'source',
      width: 120,
      render: (src: Source | null | undefined, row) =>
        src ? (
          <Tag color={THIRD_PARTY.has(src) ? 'purple' : 'default'}>
            {THIRD_PARTY.has(src) ? `${src} (3rd party)` : src}
          </Tag>
        ) : (
          <Typography.Text type="secondary">
            {row.errorType ?? '—'}
          </Typography.Text>
        ),
    },
    {
      title: 'Type',
      dataIndex: 'errorType',
      width: 160,
      render: (t: ErrorType | null | undefined) =>
        t ? <code style={{ fontSize: 11 }}>{t}</code> : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Where',
      dataIndex: 'screen',
      width: 150,
      render: (screen: string | null | undefined, row) =>
        screen || row.feature ? (
          <Space direction="vertical" size={0}>
            <span>{screen ?? '—'}</span>
            {row.feature ? (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {row.feature}
              </Typography.Text>
            ) : null}
          </Space>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Message',
      dataIndex: 'message',
      ellipsis: true,
      render: (m: string, row) => {
        // `occurrences` is the real column; `context.duplicateCount` is where
        // the old dedup kept its tally, so history still shows a count.
        const n =
          row.occurrences ?? (row.context?.duplicateCount as number | undefined);
        return (
          <Space size={6}>
            <span>{m}</span>
            {n && n > 1 ? (
              <Tag color="warning" style={{ fontSize: 10 }}>
                ×{n}
              </Tag>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: 'Version',
      dataIndex: 'appVersion',
      width: 90,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s: Status) => <Tag color={STATUS_COLOR[s]}>{plain(s)}</Tag>,
      width: 110,
    },
    {
      title: '',
      width: 90,
      render: (_: unknown, row: Incident) => (
        <Button size="small" onClick={() => openIncident(row)}>
          Open
        </Button>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
          App problems
          <Tag color="success" style={{ marginLeft: 12 }}>
            live · 5s
          </Tag>
        </Typography.Title>
        <Button icon={<ReloadOutlined />} onClick={() => invalidate()}>
          Refresh
        </Button>
      </Space>

      {/* Stat counters — last 24h breakdown plus open backlog. */}
      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Open"
              value={stats.data?.open ?? 0}
              valueStyle={{ color: (stats.data?.open ?? 0) > 0 ? '#EF4444' : undefined }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Fatal (24h)"
              value={stats.data?.fatal24h ?? 0}
              valueStyle={{ color: (stats.data?.fatal24h ?? 0) > 0 ? '#FB3C7F' : undefined }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="Errors (24h)" value={stats.data?.error24h ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="All last 24h" value={stats.data?.last24h ?? 0} />
          </Card>
        </Col>
      </Row>

      {/* Filter bar. Selecting 'All' on any filter clears it via the Select
          allowClear behaviour. sinceMinutes presets are the common windows
          an operator wants to inspect when they get pinged in chat. */}
      <Card>
        <Space wrap>
          <Select<Status | undefined>
            allowClear
            placeholder="Status"
            style={{ width: 150 }}
            value={status}
            onChange={(v) => setStatus(v)}
            options={[
              { value: 'open', label: plain('open') },
              { value: 'acknowledged', label: plain('acknowledged') },
              { value: 'resolved', label: plain('resolved') },
              { value: 'ignored', label: plain('ignored') },
            ]}
          />
          <Select<Severity | undefined>
            allowClear
            placeholder="Severity"
            style={{ width: 130 }}
            value={severity}
            onChange={(v) => setSeverity(v)}
            options={[
              { value: 'fatal', label: plain('fatal') },
              { value: 'error', label: plain('error') },
              { value: 'warning', label: plain('warning') },
              { value: 'info', label: plain('info') },
            ]}
          />
          <Select<Category | undefined>
            allowClear
            placeholder="Category"
            style={{ width: 150 }}
            value={category}
            onChange={(v) => setCategory(v)}
            options={[
              { value: 'crash', label: plain('crash') },
              { value: 'network', label: plain('network') },
              { value: 'call', label: plain('call') },
              { value: 'permission', label: plain('permission') },
              { value: 'navigation', label: plain('navigation') },
              { value: 'ui', label: plain('ui') },
              { value: 'other', label: plain('other') },
            ]}
          />
          <Select<Platform | undefined>
            allowClear
            placeholder="Platform"
            style={{ width: 120 }}
            value={platform}
            onChange={(v) => setPlatform(v)}
            options={[
              { value: 'ios', label: 'iOS' },
              { value: 'android', label: 'Android' },
              { value: 'web', label: 'Web' },
              { value: 'admin', label: 'Admin' },
            ]}
          />
          <Select<number | undefined>
            allowClear
            placeholder="Window"
            style={{ width: 130 }}
            value={sinceMinutes}
            onChange={(v) => setSinceMinutes(v)}
            options={[
              { value: 15, label: 'Last 15 min' },
              { value: 60, label: 'Last 1 hour' },
              { value: 6 * 60, label: 'Last 6 hours' },
              { value: 24 * 60, label: 'Last 24 hours' },
              { value: 7 * 24 * 60, label: 'Last 7 days' },
            ]}
          />
        </Space>
      </Card>

      <Card>
        {list.isError && !list.data ? (
          <QueryError error={list.error} what="app problems" onRetry={() => void list.refetch()} />
        ) : list.data && list.data.items.length === 0 ? (
          <Empty description="No incidents match these filters." />
        ) : (
          <Table<Incident>
            rowKey="id"
            loading={list.isLoading}
            dataSource={list.data?.items ?? []}
            columns={columns}
            pagination={{ pageSize: 25 }}
            size="small"
            rowClassName={(r) =>
              r.severity === 'fatal' ? 'maja-fatal-row' : r.severity === 'error' ? 'maja-error-row' : ''
            }
          />
        )}
      </Card>

      <Drawer
        width={760}
        open={!!selected}
        onClose={closeSelected}
        title={selected ? selected.message.slice(0, 80) : 'Incident'}
        extra={
          selected && (selected.status === 'open' || selected.status === 'acknowledged') ? (
            <Space>
              {/* An acknowledged incident still needs closing: keep Resolve / Ignore, drop Acknowledge. */}
              {selected.status === 'open' && <Can permission="incidents.acknowledge"><Popconfirm
                title="Acknowledge?"
                description="Marks as triaged; stops showing up in the 'Open' filter."
                onConfirm={() =>
                  act.mutate({ id: selected.id, verb: 'acknowledge', note: ackNote })
                }
              >
                <Button type="primary" icon={<CheckCircleOutlined />}>
                  Acknowledge
                </Button>
              </Popconfirm></Can>}
              <Can permission="incidents.resolve"><Popconfirm
                title="Resolve?"
                description="Marks as fixed/dealt-with."
                onConfirm={() =>
                  act.mutate({ id: selected.id, verb: 'resolve', note: ackNote })
                }
              >
                <Button icon={<CheckCircleOutlined />}>Resolve</Button>
              </Popconfirm>
              <Popconfirm
                title="Ignore?"
                description="Marks as a known false-positive / not worth fixing."
                onConfirm={() =>
                  act.mutate({ id: selected.id, verb: 'ignore', note: ackNote })
                }
              >
                <Button icon={<EyeInvisibleOutlined />} danger>
                  Ignore
                </Button>
              </Popconfirm></Can>
            </Space>
          ) : selected ? (
            <Tag color={STATUS_COLOR[selected.status]}>{plain(selected.status)}</Tag>
          ) : null
        }
      >
        {selected && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Space wrap>
              <Tag color={SEVERITY_COLOR[selected.severity]}>{plain(selected.severity)}</Tag>
              <Tag>{plain(selected.category)}</Tag>
              <Tag>{plain(selected.platform)}</Tag>
              <Tag>
                v{selected.appVersion}
                {selected.buildNumber ? ` (${selected.buildNumber})` : ''}
              </Tag>
              {selected.osVersion && <Tag>OS {selected.osVersion}</Tag>}
              {selected.environment && (
                <Tag color={selected.environment === 'production' ? 'red' : 'default'}>
                  {selected.environment}
                </Tag>
              )}
              {/* Stated plainly: this one is not ours to fix. */}
              {selected.source && THIRD_PARTY.has(selected.source) && (
                <Tag color="purple">third party · {selected.source}</Tag>
              )}
            </Space>
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              {selected.message}
            </Typography.Paragraph>

            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Problem type">
                {selected.errorType ? plain(selected.errorType) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Source">
                {selected.source ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Feature">
                {selected.feature ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Screen">
                {selected.screen ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="User role">
                {selected.userRole ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Occurrences">
                {selected.occurrences ??
                  (selected.context?.duplicateCount as number | undefined) ??
                  1}
              </Descriptions.Item>
              <Descriptions.Item label="First seen">
                {when(selected.firstSeenAt ?? selected.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Last seen">
                {when(
                  selected.lastSeenAt ??
                    (selected.context?.lastSeenAt as string | undefined) ??
                    selected.createdAt,
                )}
              </Descriptions.Item>
            </Descriptions>

            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {selected.userId
                ? `user ${selected.userId.slice(0, 8)}…`
                : 'anonymous (pre-OTP)'}{' '}
              · device {selected.deviceId.slice(0, 12)}
              {selected.fingerprint
                ? ` · group ${selected.fingerprint.slice(0, 8)}`
                : ''}
            </Typography.Text>

            {selected.context && Object.keys(selected.context).length > 0 && (
              <Card size="small" title="Context">
                <pre
                  style={{
                    color: '#9CA3AF',
                    fontSize: 12,
                    background: '#0A0A0A',
                    padding: 12,
                    borderRadius: 8,
                    overflowX: 'auto',
                    maxHeight: 240,
                  }}
                >
                  {JSON.stringify(selected.context, null, 2)}
                </pre>
              </Card>
            )}

            {selected.stackTrace && (
              <Card size="small" title="Stack trace">
                <pre
                  style={{
                    color: '#FCA5A5',
                    fontSize: 11,
                    background: '#0A0A0A',
                    padding: 12,
                    borderRadius: 8,
                    overflowX: 'auto',
                    maxHeight: 360,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {selected.stackTrace}
                </pre>
              </Card>
            )}

            {selected.status === 'open' && (
              <Card size="small" title="Note (optional, written to audit log)">
                <Input.TextArea
                  rows={2}
                  value={ackNote}
                  onChange={(e) => setAckNote(e.target.value)}
                  placeholder="e.g. 'fixed in v0.5.2 release' or 'known issue on Android 14, retrying via toast'"
                />
              </Card>
            )}

            {selected.ackNote && (
              <Card size="small" title="Resolution note">
                <Typography.Text>{selected.ackNote}</Typography.Text>
              </Card>
            )}
          </Space>
        )}
      </Drawer>
    </Space>
  );
}
